import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import Pressable from '@atlaskit/primitives/pressable';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import type { PresenceContentType, PresenceContentMode } from '../presenceTypes';

type AddPeopleProps = {
	contentType: PresenceContentType;
	contentMode: PresenceContentMode;
	contentId: string;
	analyticsTriggerSource?: string;
	toggleEmptyPresenceDialog: () => void;
	isPopupOpen: boolean;
};

const i18n = defineMessages({
	addPeopleLabel: {
		id: 'team-presence.avatar-button.add-people-label',
		defaultMessage: 'Add people to this {contentType}',
		description: 'Accessible label for presence avatar add people button',
	},
	contentTypeLabel: {
		id: 'team-presence.avatar-button.content-type-label',
		defaultMessage:
			'{contentType, select, page {page} blogpost {blog post} whiteboard {whiteboard} livepage {live doc} database {database} other {content}}',
		description: 'Label for content type in presence avatar add people button',
	},
});

const iconButtonStyles = xcss({
	marginInlineEnd: 'space.0',
	marginInlineStart: 'space.negative.050',
});

const iconButtonLegacyMarginStyles = xcss({
	marginInlineEnd: 'space.025',
	marginInlineStart: 'space.negative.075',
});

const dialogSelectedStyles = xcss({
	backgroundColor: 'color.background.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

const pressableStyles = xcss({
	height: '28px',
	width: '28px',
	borderRadius: 'radius.full',
	color: 'color.text.subtle',
	fontWeight: 'font.weight.medium',
	backgroundColor: 'color.background.accent.gray.subtlest',
	padding: 'space.050',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	':hover': {
		textDecoration: 'underline',
		backgroundColor: 'color.background.accent.gray.subtlest.hovered',
	},
	':active': {
		color: 'color.link.pressed',
		backgroundColor: 'color.background.accent.gray.subtlest.pressed',
	},
});
export const AddPeopleButton = ({
	contentType,
	contentMode,
	contentId,
	analyticsTriggerSource = 'PresenceAvatarGroup',
	toggleEmptyPresenceDialog,
	isPopupOpen,
}: AddPeopleProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getPersonAddButtonOnClickHandler = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsTriggerSource,
				product: 'confluence',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addCollaboratorsButton',
				attributes: {
					contentId,
					contentMode,
					contentType,
				},
			},
		}).fire();
		return toggleEmptyPresenceDialog();
	};

	const contentTypeLabel = formatMessage(i18n.contentTypeLabel, {
		contentType: contentType === 'page' && contentMode === 'live' ? 'livepage' : contentType,
	});

	const addPeopleButtonLabel = formatMessage(i18n.addPeopleLabel, {
		contentType: contentTypeLabel,
	});

	return (
		<Box
			xcss={[
				iconButtonStyles,
				!fg('platform-avatar-group-spacing-fix') && iconButtonLegacyMarginStyles,
			]}
		>
			<Tooltip content={addPeopleButtonLabel}>
				{(tooltipProps) => (
					<Pressable
						{...tooltipProps}
						xcss={[pressableStyles, isPopupOpen && dialogSelectedStyles]}
						aria-label={addPeopleButtonLabel}
						testId="team-presence-avatar-add-people-button"
						onClick={getPersonAddButtonOnClickHandler}
					>
						<PersonAddIcon
							label=""
							testId="team-presence-avatar-add-people-icon"
							color={isPopupOpen ? token('color.icon.selected') : token('color.icon.subtle')}
						/>
					</Pressable>
				)}
			</Tooltip>
		</Box>
	);
};
